import { createContext, useContext, useState, useEffect } from 'react';
import theme from './theme';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Check localStorage or system preference for initial theme
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('nuvia-theme');
    if (savedTheme) {
      return savedTheme;
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  const [mode, setMode] = useState(getInitialTheme);

  // Update localStorage and apply theme when mode changes
  useEffect(() => {
    localStorage.setItem('nuvia-theme', mode);
    document.documentElement.setAttribute('data-theme', mode);

    // Apply CSS variables
    applyThemeVariables(mode);
  }, [mode]);

  const applyThemeVariables = (currentMode) => {
    const root = document.documentElement;
    const colors = theme.colors[currentMode];

    // Background colors
    root.style.setProperty('--bg-primary', colors.bg.primary);
    root.style.setProperty('--bg-secondary', colors.bg.secondary);
    root.style.setProperty('--bg-tertiary', colors.bg.tertiary);
    root.style.setProperty('--bg-elevated', colors.bg.elevated);
    root.style.setProperty('--bg-overlay', colors.bg.overlay);

    // Text colors
    root.style.setProperty('--text-primary', colors.text.primary);
    root.style.setProperty('--text-secondary', colors.text.secondary);
    root.style.setProperty('--text-tertiary', colors.text.tertiary);
    root.style.setProperty('--text-inverse', colors.text.inverse);

    // Accent colors
    root.style.setProperty('--accent-primary', colors.accent.primary);
    root.style.setProperty('--accent-secondary', colors.accent.secondary);
    root.style.setProperty('--accent-tertiary', colors.accent.tertiary);
    root.style.setProperty('--accent-hover', colors.accent.hover);
    root.style.setProperty('--accent-active', colors.accent.active);

    // Semantic colors
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-info', colors.info);

    // Event/UI colors (for buttons, badges, etc.)
    root.style.setProperty('--color-green', colors.success);
    root.style.setProperty('--color-red', colors.error);
    root.style.setProperty('--color-orange', colors.warning);
    root.style.setProperty('--color-blue', colors.info);
    root.style.setProperty('--color-pink', currentMode === 'dark' ? '#ec4899' : '#db2777');
    root.style.setProperty('--color-purple', colors.accent.primary);

    // Alpha (transparent) versions for backgrounds
    root.style.setProperty('--color-green-alpha', currentMode === 'dark' ? 'rgba(109, 212, 126, 0.1)' : 'rgba(60, 180, 75, 0.1)');
    root.style.setProperty('--color-red-alpha', currentMode === 'dark' ? 'rgba(255, 107, 138, 0.1)' : 'rgba(230, 57, 70, 0.1)');
    root.style.setProperty('--color-orange-alpha', currentMode === 'dark' ? 'rgba(255, 182, 72, 0.1)' : 'rgba(255, 159, 28, 0.1)');
    root.style.setProperty('--color-blue-alpha', currentMode === 'dark' ? 'rgba(94, 179, 255, 0.1)' : 'rgba(25, 130, 196, 0.1)');
    root.style.setProperty('--color-pink-alpha', 'rgba(236, 72, 153, 0.1)');
    root.style.setProperty('--color-purple-alpha', 'rgba(157, 92, 255, 0.1)');
    root.style.setProperty('--accent-primary-alpha', 'rgba(157, 92, 255, 0.1)');

    // Border colors
    root.style.setProperty('--border-default', colors.border.default);
    root.style.setProperty('--border-focus', colors.border.focus);
    root.style.setProperty('--border-hover', colors.border.hover);

    // Typography (mode-independent)
    root.style.setProperty('--font-primary', theme.typography.fonts.primary);
    root.style.setProperty('--font-mono', theme.typography.fonts.mono);
    root.style.setProperty('--font-heading', theme.typography.fonts.heading);

    // Transitions
    root.style.setProperty('--transition-fast', theme.transitions.fast);
    root.style.setProperty('--transition-base', theme.transitions.base);
    root.style.setProperty('--transition-slow', theme.transitions.slow);
  };

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const value = {
    mode,
    toggleTheme,
    theme,
    colors: theme.colors[mode],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
